import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Trash2, X, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const { getAuthHeader } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ url: '', title: '', category: 'interior', motorhome: '' });

  const categories = ['exterior', 'interior', 'lifestyle', 'destinations'];

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/gallery`, getAuthHeader());
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/gallery`, formData, getAuthHeader());
      fetchImages();
      resetForm();
    } catch (error) {
      console.error('Error saving image:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/gallery/${id}`, getAuthHeader());
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const resetForm = () => {
    setFormData({ url: '', title: '', category: 'interior', motorhome: '' });
    setShowForm(false);
  };

  const groupedImages = categories.map(cat => ({
    category: cat,
    images: images.filter(img => img.category === cat)
  }));

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gallery</h1>
          <p className="text-slate-600">Manage gallery images ({images.length} total)</p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" /> Add Image
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Add New Image</CardTitle>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Image URL</Label>
                  <Input value={formData.url} onChange={(e) => setFormData({...formData, url: e.target.value})} placeholder="https://example.com/image.jpg" required />
                </div>
                <div>
                  <Label>Title</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
                </div>
                <div>
                  <Label>Category</Label>
                  <select className="w-full border rounded-md p-2" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Motorhome (optional)</Label>
                  <Input value={formData.motorhome} onChange={(e) => setFormData({...formData, motorhome: e.target.value})} placeholder="Auto-Roller 747" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit"><Save className="h-4 w-4 mr-2" /> Add Image</Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-8">
        {groupedImages.map((group) => (
          <div key={group.category}>
            <h2 className="text-lg font-semibold text-slate-800 mb-3 capitalize">{group.category} ({group.images.length})</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {group.images.map((image) => (
                <div key={image.id} className="group relative">
                  <img 
                    src={image.url} 
                    alt={image.title} 
                    className="w-full h-32 object-cover rounded-lg"
                    onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error'}
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => handleDelete(image.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-600 mt-1 truncate">{image.title}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminGallery;
